import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Bot, User, Phone } from "lucide-react";

import { outcomeLabel } from "@/lib/labels";

const outcomeColor = {
  appointment_booked: "bg-emerald-50 text-emerald-700",
  appointment_cancelled: "bg-orange-50 text-orange-700",
  human_transfer: "bg-purple-50 text-purple-700",
  question_answered: "bg-blue-50 text-blue-700",
  info_request: "bg-slate-100 text-slate-700",
  failed: "bg-red-50 text-red-700",
};

export default function Calls() {
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [err, setErr] = useState(false);
  const [showAll, setShowAll] = useState(false);
  useEffect(() => { api.get("/calls").then(r => setItems(r.data)).catch(() => setErr(true)); }, []);
  const rows = showAll ? items : items.slice(0, 50);
  return (
    <div>
      <h1 className="text-3xl font-bold tracking-tight">Calls</h1>
      <p className="text-slate-600 mt-1">Every call ASFA handled, with full transcripts.</p>
      <div className="mt-6 bg-white border border-slate-200 rounded-2xl overflow-hidden">
        {err ? (
          <div className="p-10 text-center" data-testid="calls-error">
            <p className="text-slate-600 font-medium">Couldn't load your calls right now.</p>
            <p className="text-sm text-slate-400 mt-1">Check your connection and refresh the page.</p>
          </div>
        ) : items.length === 0 ? (
          <div className="p-10 text-center text-slate-500">
            <Phone className="w-10 h-10 mx-auto text-slate-300 mb-3" />
            <p>No calls yet. Once ASFA answers your line, they'll appear here.</p>
          </div>
        ) : (
          <div className="overflow-x-auto"><table className="w-full text-sm min-w-[640px]" data-testid="calls-table">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
              <tr><th className="text-left px-6 py-3">Caller</th><th className="text-left px-6 py-3">When</th><th className="text-left px-6 py-3">Duration</th><th className="text-left px-6 py-3">Outcome</th><th className="text-left px-6 py-3">Summary</th></tr>
            </thead>
            <tbody>
              {rows.map(c => (
                <tr key={c.id} className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer" onClick={() => setSelected(c)} data-testid={`call-row-${c.id}`}>
                  <td className="px-6 py-4 font-medium">{c.caller_phone}</td>
                  <td className="px-6 py-4 text-slate-500">{new Date(c.start_at).toLocaleString()}</td>
                  <td className="px-6 py-4">{c.duration_sec}s</td>
                  <td className="px-6 py-4"><span className={`text-xs px-2 py-1 rounded-full ${outcomeColor[c.outcome] || "bg-slate-100"}`}>{c.outcome ? outcomeLabel(c.outcome) : "—"}</span></td>
                  <td className="px-6 py-4 text-slate-600 max-w-md truncate">{c.summary || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {!showAll && items.length > 50 && (
            <div className="p-3 text-center border-t border-slate-100">
              <button data-testid="calls-load-more" onClick={() => setShowAll(true)}
                className="text-sm text-blue-600 hover:underline">Show all {items.length} calls</button>
            </div>
          )}
          </div>
        )}
      </div>

      <Dialog open={!!selected} onOpenChange={() => setSelected(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Call · {selected?.caller_phone}</DialogTitle></DialogHeader>
          {selected && (
            <div>
              <div className="text-xs text-slate-500 mb-4">{new Date(selected.start_at).toLocaleString()} · {selected.duration_sec}s · <span className="font-semibold">{outcomeLabel(selected.outcome)}</span></div>
              {selected.summary && <div className="mb-4 p-3 bg-blue-50 border border-blue-100 rounded-xl text-sm text-blue-900">{selected.summary}</div>}
              <div className="space-y-2 max-h-80 overflow-y-auto" data-testid="call-transcript">
                {(selected.transcript || []).map((t, i) => (
                  <div key={`t-${i}-${t.role}-${(t.text || "").length}`} className={`flex gap-2 ${t.role === "customer" ? "justify-end" : "justify-start"}`}>
                    {t.role === "ai" && <div className="w-7 h-7 rounded-full bg-blue-600 flex-shrink-0 flex items-center justify-center"><Bot className="w-3.5 h-3.5 text-white" /></div>}
                    <div className={`max-w-md rounded-2xl px-3 py-2 text-sm ${t.role === "customer" ? "bg-slate-950 text-white" : "bg-slate-100 text-slate-900"}`}>{t.text}</div>
                    {t.role === "customer" && <div className="w-7 h-7 rounded-full bg-slate-300 flex-shrink-0 flex items-center justify-center"><User className="w-3.5 h-3.5 text-slate-700" /></div>}
                  </div>
                ))}
              </div>
              {selected.ai_actions?.length > 0 && (
                <div className="mt-4 pt-4 border-t border-slate-200">
                  <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">AI actions</div>
                  <div className="space-y-1">
                    {selected.ai_actions.map((a, i) => (
                      <div key={`${a.name}-${i}`} className="text-xs text-slate-600">→ <span className="font-mono">{a.name}</span> · {a.result?.ok !== false ? "✓" : "✗"}</div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
